import withHydrationSafeWrapper from '../../HOCs/withHydrationSafeWrapper';
import { Heading } from '../../typography/heading/Heading';
import { CarretArrowDown, CarretArrowUp } from '../../atoms/icons';
import styles from './SeoTextArea.module.scss';
import clsx from 'clsx';
import React, { CSSProperties, useCallback, useState } from 'react';
import { StylesCustomizer } from '../stylesCustomizer';

export interface SeoTextAreaProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  titleStyles?: CSSProperties;
  text?: string;
  textStyles?: CSSProperties;
  className?: string;
  textAlign?: string;
  placement?: string;
  showReadmore?: boolean;
  charLimit?: number;
  ellipsis?: boolean;
}

const SeoTextArea = React.forwardRef<HTMLDivElement, SeoTextAreaProps>(
  (
    {
      title,
      titleStyles,
      text,
      textStyles,
      className,
      textAlign = 'left',
      placement,
      showReadmore,
      charLimit = 200,
      ellipsis,
      ...rest
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const strippedText = !!text && text?.replace(/<br\s*\/?>|<p>|<\/p>/g, '');

    const containsHTML = (str: string) => /<[a-z][\s\S]*>/i.test(str);

    const getTag = (str: string) => {
      const matchTags = str.match(/<[^>]*?>/g);
      if (!matchTags) return null;

      return matchTags[0].replace(/[^a-zA-Z0-9 ]/g, '');
    };

    const shortText =
      strippedText.length &&
      strippedText
        .slice(0, charLimit)
        .replace(/[\s\n]+$/, '')
        .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]+$/, '');

    const ReadMore = useCallback(
      () => (
        <span className={styles.readMoreButton} role="presentation" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? 'Read less' : 'Read more'}
          {isOpen ? <CarretArrowUp /> : <CarretArrowDown />}
        </span>
      ),
      [isOpen],
    );

    if (!title && !text) return null;

    const SeoTitle = (variables: CSSProperties) => {
      return (
        <Heading
          as={containsHTML(title) ? getTag(title) : 'h2'}
          styleAs="h1"
          className={styles.heading}
          weight="light"
          variables={variables}
        >
          {containsHTML(title) ? title.replace(/<(?:.|\n)*?>/gm, '') : title}
        </Heading>
      );
    };

    const SeoText = (variables: CSSProperties) => {
      return <p className={styles.text} dangerouslySetInnerHTML={{ __html: text }} style={{ ...variables }} />;
    };

    const SeoLongText = (variables: CSSProperties) => {
      return (
        <p
          className={styles.text}
          dangerouslySetInnerHTML={{ __html: !isOpen ? shortText : text }}
          style={{ ...variables }}
        />
      );
    };

    const StyledSeoTitle = StylesCustomizer(SeoTitle);
    const StyledSeoText = StylesCustomizer(SeoText);
    const StyledSeoLongText = StylesCustomizer(SeoLongText);

    return (
      <div style={{ '--textAlign': textAlign } as CSSProperties} className={clsx(styles.root, className)} {...rest}>
        <div
          className={clsx(styles.wrapper, {
            [styles.sportscraftPlp]: placement === 'sportscraft',
            [styles.jagPlp]: placement === 'jag',
          })}
        >
          {/*@ts-ignore*/}
          {title && <StyledSeoTitle styles={titleStyles} />}
          {/*@ts-ignore*/}
          {text && !showReadmore && !isOpen && <StyledSeoText styles={textStyles} />}
          {text && showReadmore ? (
            <>
              {/*@ts-ignore*/}
              <StyledSeoLongText styles={textStyles} />
              <ReadMore />
            </>
          ) : null}
        </div>
      </div>
    );
  },
);

export default withHydrationSafeWrapper(SeoTextArea);